import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const Industry = () => {
  const { industry } = useParams();
  const { language } = useLanguage();

  const industries = {
    health: {
      name: language === 'es' ? 'Salud' : 'Health',
      description: language === 'es'
        ? 'Mejora la atención al paciente con respuestas rápidas y agendamiento automatizado'
        : 'Improve patient care with quick responses and automated scheduling',
      benefits: [
        language === 'es' ? 'Confirmación automática de citas médicas' : 'Automatic medical appointment confirmation',
        language === 'es' ? 'Recordatorios de medicación y seguimiento' : 'Medication reminders and follow-up',
        language === 'es' ? 'Atención 24/7 para consultas de emergencia' : '24/7 support for emergency inquiries',
        language === 'es' ? 'Agendamiento inteligente de consultas' : 'Intelligent appointment scheduling',
        language === 'es' ? 'Integración con sistemas de historia clínica' : 'Integration with medical record systems'
      ]
    },
    realestate: {
      name: language === 'es' ? 'Inmobiliario' : 'Real Estate',
      description: language === 'es'
        ? 'Cierra más propiedades con contacto rápido y seguimiento efectivo'
        : 'Close more properties with quick contact and effective follow-up',
      benefits: [
        language === 'es' ? 'Contacto en menos de 1 minuto' : 'Contact in less than 1 minute',
        language === 'es' ? 'Agendamiento automático de visitas' : 'Automatic visit scheduling',
        language === 'es' ? 'Seguimiento personalizado por propiedad' : 'Personalized follow-up by property',
        language === 'es' ? 'Calificación de leads por presupuesto' : 'Lead qualification by budget',
        language === 'es' ? 'Información instantánea sobre propiedades' : 'Instant property information'
      ]
    },
    insurance: {
      name: language === 'es' ? 'Seguros' : 'Insurance',
      description: language === 'es'
        ? 'Aumenta tus ventas de pólizas con seguimiento automatizado y atención personalizada'
        : 'Increase policy sales with automated follow-up and personalized attention',
      benefits: [
        language === 'es' ? 'Cotización instantánea de pólizas' : 'Instant policy quotes',
        language === 'es' ? 'Seguimiento de renovaciones automático' : 'Automatic renewal follow-up',
        language === 'es' ? 'Atención de reclamos 24/7' : '24/7 claims support',
        language === 'es' ? 'Calificación inteligente de prospectos' : 'Intelligent prospect qualification',
        language === 'es' ? 'Recordatorios de vencimiento de pólizas' : 'Policy expiration reminders'
      ]
    }
  };

  const currentIndustry = industries[industry] || industries.health;

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-900/20 via-black to-emerald-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-green-600/20 text-green-400 border-green-600/30 mb-6 px-4 py-2">
              {language === 'es' ? 'Industria' : 'Industry'}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                {currentIndustry.name}
              </span>
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              {currentIndustry.description}
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white rounded-full px-8 py-6 text-lg flex items-center space-x-2 mx-auto shadow-lg shadow-green-600/50">
                <span>{language === 'es' ? 'Solicitar Demo' : 'Request Demo'}</span>
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              {language === 'es' ? 'Beneficios para tu Industria' : 'Benefits for your Industry'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {currentIndustry.benefits.map((benefit, index) => (
                <Card key={index} className="bg-gradient-to-br from-gray-900 to-black border-green-900/30">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    </div>
                    <span className="text-lg pt-2 text-white">{benefit}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industry;